import React, { useState, useEffect } from 'react';
import { Activity, TrendingUp, Clock, CheckCircle2, XCircle, Eye, Download, ImageIcon, Sparkles, ExternalLink, Copy, Check, Sliders, ArrowRight } from 'lucide-react';
import { UsageChart } from './UsageChart';
import { ImagePreviewModal, ImagePreviewRecord } from './ImagePreviewModal';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

export function ApiUsageStats() {
    const { user } = useAuth();
    const [usageData, setUsageData] = useState<ImagePreviewRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalCalls: 0,
        successfulCalls: 0,
        failedCalls: 0,
        lastCallTime: null as Date | null,
        dailyStats: [] as { date: string; count: number }[]
    });
    const [previewItem, setPreviewItem] = useState<ImagePreviewRecord | null>(null);
    const [copiedUrlId, setCopiedUrlId] = useState<string | null>(null);

    useEffect(() => {
        if (user?.userId) {
            fetchUsageData();
        }
    }, [user?.userId, user?.lastLogReset]);

    const fetchUsageData = async () => {
        if (!user?.userId) return;

        try {
            setLoading(true);

            // Fetch directly from Firebase Firestore
            const logsRef = collection(db, 'bgusers', user.userId, 'api_usage');
            const q = query(logsRef, orderBy('timestamp', 'desc'), limit(10));
            const querySnapshot = await getDocs(q);

            const records: ImagePreviewRecord[] = [];
            let successCount = 0;
            let failedCount = 0;

            querySnapshot.forEach((docSnap: any) => {
                const item = docSnap.data();
                const success = item.success !== false;
                if (success) successCount++;
                else failedCount++;

                records.push({
                    id: docSnap.id,
                    timestamp: item.timestamp?.toDate ? item.timestamp.toDate() : new Date(item.timestamp || Date.now()),
                    endpoint: item.endpoint || '',
                    success: success,
                    error: item.error,
                    file_name: item.file_name || item.fileName,
                    original_url: item.original_url || item.originalUrl || item.input_url || item.inputUrl,
                    result_url: item.result_url || item.resultUrl || item.output_url || item.outputUrl || item.processedUrl,
                    duration_ms: item.duration_ms,
                    cost: item.cost
                });
            });

            setUsageData(records);
            setStats({
                totalCalls: records.length,
                successfulCalls: successCount,
                failedCalls: failedCount,
                lastCallTime: records.length > 0 ? records[0].timestamp : null,
                dailyStats: []
            });
        } catch (error) {
            console.error('Error fetching usage data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatTimestamp = (date: Date) => {
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedUrlId(id);
        setTimeout(() => setCopiedUrlId(null), 2000);
    };

    const downloadReport = () => {
        if (!stats.dailyStats || stats.dailyStats.length === 0) return;

        const headers = ['Date', 'Total Requests', 'Successful', 'Failed'];
        const csvRows = [headers.join(',')];

        stats.dailyStats.forEach((day: any) => {
            csvRows.push(`${day.date},${day.count},${day.success || 0},${day.failed || 0}`);
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', 'api_usage_report.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    if (!user?.apiKey) {
        return null;
    }

    return (
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                        <Activity className="h-4 w-4 text-muted-foreground" /> Real-time Activity
                    </h3>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-background border border-border rounded-lg p-4 flex flex-col justify-between h-28">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">Total Requests</span>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <span className="text-3xl font-bold text-foreground">{stats.totalCalls}</span>
                </div>

                <div className="bg-background border border-border rounded-lg p-4 flex flex-col justify-between h-28">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">Success Rate</span>
                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-500" />
                    </div>
                    <div>
                        <span className="text-3xl font-bold text-foreground">
                            {stats.totalCalls > 0 ? Math.round((stats.successfulCalls / stats.totalCalls) * 100) : 0}%
                        </span>
                        <p className="text-xs text-muted-foreground mt-1">{stats.successfulCalls} completed</p>
                    </div>
                </div>

                <div className="bg-background border border-border rounded-lg p-4 flex flex-col justify-between h-28">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">Errors</span>
                        <XCircle className="h-4 w-4 text-red-600 dark:text-red-500" />
                    </div>
                    <span className="text-3xl font-bold text-foreground">{stats.failedCalls}</span>
                </div>
            </div>

            {/* Advanced Analytics Card */}
            <div className="bg-background border border-border rounded-lg p-5 mb-6">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-primary" /> 30-Day Growth Trend
                    </h4>
                    <div className="flex items-center gap-2">
                        {user?.planType === 'payg' && stats.dailyStats.length > 0 && (
                            <button
                                onClick={downloadReport}
                                className="flex items-center gap-1 text-[10px] font-medium text-primary bg-primary/10 hover:bg-primary/20 hover:scale-105 px-2 py-1 rounded transition-all"
                                title="Download usage report as CSV"
                            >
                                <Download className="h-3 w-3" />
                                Download CSV
                            </button>
                        )}
                        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-widest bg-muted/50 px-2 py-1 rounded hidden sm:inline-block">Last 30 Days</span>
                    </div>
                </div>
                {user?.planType === 'payg' ? (
                    <UsageChart data={stats.dailyStats} loading={loading} />
                ) : (
                    <div className="h-[200px] flex flex-col items-center justify-center bg-muted/5 rounded-lg border border-dashed border-border mt-4">
                        <TrendingUp className="h-8 w-8 text-muted-foreground/30 mb-2" />
                        <p className="text-sm font-medium text-foreground text-center">Advanced Analytics</p>
                        <p className="text-xs text-muted-foreground mt-1 text-center">Available only for Lifetime Membership plans</p>
                        <a href="/pricing" className="mt-4 text-xs font-bold text-primary hover:underline">Upgrade Now →</a>
                    </div>
                )}
            </div>

            {/* Last Call Info */}
            {stats.lastCallTime && (
                <div className="bg-muted/20 border border-border rounded-lg p-3 mb-6 flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Last activity:</span>
                    <span className="font-medium text-foreground">{formatTimestamp(stats.lastCallTime)}</span>
                </div>
            )}

            {/* Recent Activity with Previews */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider text-xs">Recent Log Entries</h4>
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                            Input & Output Previews
                        </span>
                    </div>
                    <a
                        href="/logs"
                        className="text-sm text-primary hover:text-primary/80 font-medium transition-colors flex items-center gap-1"
                    >
                        View Full Logs →
                    </a>
                </div>

                {loading ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">Loading usage data...</div>
                ) : usageData.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm bg-muted/20 rounded-lg border border-dashed border-border">
                        No API calls recorded yet.
                    </div>
                ) : (
                    <div className="divide-y divide-border border border-border rounded-xl overflow-hidden shadow-sm bg-background">
                        {usageData.slice(0, 10).map((record: ImagePreviewRecord, index: number) => {
                            const hasImages = Boolean(record.original_url || record.result_url);
                            const recordId = record.id || `idx-${index}`;

                            return (
                                <div
                                    key={recordId}
                                    className="p-3.5 bg-background hover:bg-muted/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-3"
                                >
                                    {/* Left Info: Status, Endpoint, Filename */}
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <div className={`p-1 rounded-full flex-shrink-0 ${record.success ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                                            {record.success ? (
                                                <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
                                            ) : (
                                                <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-semibold text-foreground truncate">
                                                    {record.endpoint}
                                                </span>
                                                <span className="text-xs text-muted-foreground font-mono truncate max-w-[200px] sm:max-w-none">
                                                    {record.file_name || 'image.jpg'}
                                                </span>
                                            </div>
                                            {!record.success && record.error && (
                                                <p className="text-xs text-red-500 mt-0.5 truncate">
                                                    Error: {record.error}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Middle & Right: Image Thumbnails, Action Buttons & Timestamp */}
                                    <div className="flex items-center justify-between md:justify-end gap-3 flex-shrink-0">
                                        {hasImages ? (
                                            <div className="flex items-center gap-2 bg-muted/40 p-1.5 rounded-lg border border-border/80">
                                                {/* Input Thumbnail */}
                                                {record.original_url && (
                                                    <div className="flex items-center gap-1.5" title="Input Image (Original)">
                                                        <div
                                                            onClick={() => setPreviewItem(record)}
                                                            className="w-10 h-10 rounded-md overflow-hidden border border-border bg-background cursor-pointer hover:ring-2 hover:ring-primary transition-all relative group flex-shrink-0"
                                                        >
                                                            <img
                                                                src={record.original_url}
                                                                alt="Input"
                                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                                                            />
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                <Eye className="w-3 h-3 text-white" />
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => copyToClipboard(record.original_url!, `orig-${recordId}`)}
                                                            className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
                                                            title="Copy Input Image URL"
                                                        >
                                                            {copiedUrlId === `orig-${recordId}` ? (
                                                                <Check className="w-3.5 h-3.5 text-green-500" />
                                                            ) : (
                                                                <Copy className="w-3.5 h-3.5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                )}

                                                {record.original_url && record.result_url && (
                                                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/60 hidden sm:block" />
                                                )}

                                                {/* Output Thumbnail */}
                                                {record.result_url && (
                                                    <div className="flex items-center gap-1.5" title="Output Image (No Background)">
                                                        <div
                                                            onClick={() => setPreviewItem(record)}
                                                            className="w-10 h-10 rounded-md overflow-hidden border border-green-500/40 bg-[linear-gradient(45deg,#262626_25%,transparent_25%),linear-gradient(-45deg,#262626_25%,transparent_25%),linear-gradient(45deg,transparent_75%,#262626_75%),linear-gradient(-45deg,transparent_75%,#262626_75%)] bg-[size:8px_8px] bg-[#171717] cursor-pointer hover:ring-2 hover:ring-green-500 transition-all relative group flex-shrink-0"
                                                        >
                                                            <img
                                                                src={record.result_url}
                                                                alt="Output"
                                                                className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-200"
                                                            />
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                <Sparkles className="w-3 h-3 text-white" />
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => copyToClipboard(record.result_url!, `res-${recordId}`)}
                                                            className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
                                                            title="Copy Output Image URL"
                                                        >
                                                            {copiedUrlId === `res-${recordId}` ? (
                                                                <Check className="w-3.5 h-3.5 text-green-500" />
                                                            ) : (
                                                                <Copy className="w-3.5 h-3.5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                )}

                                                {/* View Comparison Button */}
                                                <button
                                                    onClick={() => setPreviewItem(record)}
                                                    className="px-2 py-1 text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary rounded-md transition-colors flex items-center gap-1 ml-1"
                                                    title="Open Interactive Comparison"
                                                >
                                                    <Sliders className="w-3 h-3" />
                                                    <span className="hidden sm:inline">Compare</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground/60 italic hidden sm:inline">
                                                No image payload
                                            </span>
                                        )}

                                        <div className="text-right">
                                            <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                                                {formatTimestamp(record.timestamp)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Responsive Image Preview & Comparison Modal */}
            {previewItem && (
                <ImagePreviewModal
                    record={previewItem}
                    onClose={() => setPreviewItem(null)}
                />
            )}
        </div>
    );
}


